/* ---- happiness -----------------------------------------------------------
   The read-out for shared/happy.js's mechanic.  The meter is the headline --
   it has to read from across the room, so the bar is half again the height of
   #charge's and the notch at PURR_AT turns "how close to a purr" into a
   visible distance rather than a number.  The place rows underneath are built
   once by happyhud.js from PLACES itself (the page cannot drift out of step
   with the table -- the bargain #customizer already strikes) and are updated
   through classes and custom properties only. */
#happy{width:248px;padding:12px 14px 13px;display:flex;flex-direction:column;
  gap:8px;flex:0 1 auto;min-height:0;overflow-y:auto;
  overscroll-behavior:contain;scrollbar-width:thin}
.happy-head{display:flex;align-items:center;gap:8px}
.happy-head .label:first-child{flex:1}
/* The word is most of the purr signal under prefers-reduced-motion, where the
   meter's swell is off -- same reasoning as #charge's "ready" label. */
#happy-word{color:var(--accent);display:none}
#happy.purring #happy-word{display:inline}
#mute{font-family:inherit;font-size:.58rem;letter-spacing:.12em;
  text-transform:uppercase;padding:3px 7px;color:var(--ink);background:transparent;
  border:1px solid var(--edge);border-radius:3px;cursor:pointer;
  transition:color .15s,border-color .15s,opacity .15s}
#mute:hover{border-color:rgba(241,239,232,.32)}
#mute:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
#mute.muted{color:var(--dim);opacity:.6}
#happy-meter{position:relative;margin:2px 0 0}
#happy-meter i{display:block;height:8px;border-radius:3px;background:var(--edge);
  overflow:hidden}
/* Same machinery as #charge: the fill is a scaleX of --happy, quantised in
   happyhud.js so a held value stops writing, and only ever a transform. */
#happy-meter i::before{content:"";display:block;height:100%;background:var(--ink);
  transform-origin:0 50%;transform:scaleX(var(--happy,0));transition:background .2s}
/* The purr notch.  --purr-at is written once by happyhud.js from the shared
   constant; a 72% typed here would be a second copy waiting to drift.  It
   overhangs the track 3px each side so it stays a landmark even when the fill
   is sitting exactly on it. */
#happy-meter::after{content:"";position:absolute;top:-3px;bottom:-3px;
  left:calc(var(--purr-at,.72)*100% - 1px);width:2px;border-radius:1px;
  background:var(--dim)}
#happy.purring #happy-meter i::before{background:var(--accent)}
#happy.purring #happy-meter::after{background:var(--accent)}
/* A slow swell, nothing like #charge's .55s throb: this state persists for
   minutes at a time, and anything urgent-looking that long reads as an alarm. */
#happy.purring #happy-meter i{animation:purr-swell 1.9s ease-in-out infinite}
@keyframes purr-swell{50%{transform:scale(1.005,1.25)}}
.hp-cap{font-size:.6rem;letter-spacing:.06em;color:var(--dim)}
#happy-roster{margin-top:1px}
#happy-roster:empty{display:none}
/* The hunt's collection readout -- what this cat has caught, forever, off
   the record's tally (happyhud.setTally).  Accent rather than dim because a
   trophy shelf is a boast, not a footnote; empty (a cat that has never
   hunted) it takes no space at all.  Deliberately NOT inside the 640px
   #happy-list collapse: it is one line, and on a phone it is the only
   collection the player can see. */
#happy-tally{font-size:.6rem;letter-spacing:.06em;color:var(--accent);margin-top:1px}
#happy-tally:empty{display:none}
/* The treasure readout -- the seek row's collection (happyhud.setFinds),
   on the tally's exact bargain: accent because a collection is a boast,
   hidden while empty so an unstarted hunt is not a row of nothing, and
   outside the #happy-list collapse because on a phone this line IS the
   collection.  It wraps (the found shorts can run two lines at seven of
   seven) where the tally never does; that is fine -- it grows one
   discovery at a time and each new wrap is earned. */
#happy-finds{font-size:.6rem;letter-spacing:.06em;color:var(--accent);margin-top:1px}
#happy-finds:empty{display:none}
.hp-h{font-size:.6rem;letter-spacing:.19em;text-transform:uppercase;
  color:var(--dim);margin-top:5px}
/* "Needs you" carries the accent because the solo/needs-you split IS the
   game: the six above the line fill themselves, the five below are the part a
   person does, and the panel has to say so louder than anything else on it. */
.hp-h.you{color:var(--accent)}
.hp-row{display:grid;grid-template-columns:32px 1fr 34px;gap:2px 8px;
  align-items:center}
.hp-chip{font-size:.56rem;letter-spacing:.08em;text-transform:uppercase;
  text-align:center;padding:2px 0;border-radius:3px;color:var(--dim);
  border:1px solid var(--edge)}
/* The keycap treatment from #keys: accent ground, dark ink.  On this panel it
   marks the rows where the keys are the answer.  Bare .you so #happy-next
   (which is not an .hp-row) gets the same chip when its place needs hands. */
.you .hp-chip{color:#171812;background:var(--accent);
  border-color:var(--accent);font-weight:500}
.hp-name{font-size:.7rem;color:var(--ink);line-height:1.3}
.hp-want{display:block;height:3px;border-radius:2px;background:var(--edge);
  overflow:hidden}
.hp-want::before{content:"";display:block;height:100%;background:var(--dim);
  transform-origin:0 50%;transform:scaleX(var(--want,0))}
.hp-row.you .hp-want::before{background:var(--accent)}
/* Recently banked: the want is empty, so the row steps back out of the way of
   the ones still asking. */
.hp-row.done .hp-name{color:var(--dim);opacity:.62}
.hp-row.done .hp-chip{opacity:.5}
.hp-row.live .hp-name{color:var(--accent)}
.hp-prog{grid-column:2/4;display:none;font-size:.64rem;letter-spacing:.04em;
  color:var(--accent);align-items:center;gap:7px}
.hp-row.live .hp-prog{display:flex}
.hp-prog span{white-space:nowrap}
.hp-prog i{flex:1;display:block;height:3px;border-radius:2px;
  background:var(--edge);overflow:hidden}
.hp-prog i::before{content:"";display:block;height:100%;background:var(--accent);
  transform-origin:0 50%;transform:scaleX(var(--t,0))}
.hp-hint{grid-column:2/4;font-size:.62rem;line-height:1.45;color:var(--dim);
  max-height:0;opacity:0;overflow:hidden;
  transition:max-height .18s ease,opacity .18s ease}
.hp-row.you:hover .hp-hint,.hp-row.live .hp-hint{max-height:4em;opacity:1}
/* Phone fallback (see the 640px block): the list collapses and this one line
   carries the game -- the single most-wanted place, chip and hint included. */
#happy-next{display:none;grid-template-columns:32px 1fr;gap:2px 8px;
  align-items:center}
#happy-next .hp-h{grid-column:1/3;margin-top:0}
#happy-next .hp-hint{grid-column:2;max-height:4em;opacity:1}

/* ---- bliss toast ---------------------------------------------------------
   Somebody banked a happy place.  Top-centre because the corners are all
   spoken for and the moment belongs to everyone on the street, not to one
   panel; entry/exit borrowed from #charge (quick in, slower out). */
/* A toast, never a control: pointer-events was auto, which on a phone put a
   dead patch across the top of the screen for three seconds every time
   anybody's cat banked a place. */
#bliss{top:16px;left:50%;padding:9px 15px;z-index:4;border-color:var(--accent);
  pointer-events:none;
  display:flex;gap:9px;align-items:baseline;opacity:0;visibility:hidden;
  transform:translate(-50%,-8px);
  transition:opacity .26s ease-in,transform .26s ease-in,visibility 0s .26s}
#bliss.show{opacity:1;visibility:visible;transform:translate(-50%,0);
  transition:opacity .12s ease-out,transform .12s ease-out}
#bliss b{font-family:"Archivo",system-ui,sans-serif;font-weight:800;
  font-size:.92rem;letter-spacing:-.02em;white-space:nowrap}
#bliss span{font-size:.64rem;letter-spacing:.13em;text-transform:uppercase;
  color:var(--accent);white-space:nowrap}
@media (prefers-reduced-motion:reduce){
  /* The purr swell goes; #happy-word and the accent fill carry the state. */
  #happy.purring #happy-meter i{animation:none}
  .hp-hint{transition:none}
  #bliss{transform:translate(-50%,0);transition:opacity .12s,visibility 0s .12s}
  #bliss.show{transition:opacity .12s}
}
@media (max-width:640px){
  /* A phone gets the meter and the single next thing to do; eleven rows of
     list is a desktop's worth of reading and most of a phone's height. */
  #happy{width:min(248px,calc(100vw - 32px))}
  #happy-list,#happy-roster,.hp-cap{display:none}
  #happy-next{display:grid}
}
